import { onBrand } from '@foodamigos/ui';
import { BRAND_MODES } from '../branding/brands';
import { hexToRgbChannels } from '../branding/brandTheme';

// The AI accents of a neutral dashboard: greys from the design system's neutral ramp.
const NEUTRAL_AI = {
  '--fa-ai-from': 'var(--fa-neutral-500)',
  '--fa-ai-to': 'var(--fa-neutral-800)',
};

/**
 * The brand colours the design system derives from `--fa-brand` (tokens.css). It derives them on `:root`, so a
 * `--fa-brand` set on the panel's root alone would leave the buttons on the default brand (with the panel's on-brand
 * text on them): the panel's root declares them again, so they follow its own brand.
 */
const BRAND_COLORS = {
  '--fa-color-brand': 'var(--fa-brand, var(--fa-brand-default))',
  '--fa-color-brand-hover': 'color-mix(in oklab, var(--fa-color-brand), black 14%)',
  '--fa-color-brand-active': 'color-mix(in oklab, var(--fa-color-brand), black 24%)',
  '--fa-color-brand-subtle': 'color-mix(in oklab, var(--fa-color-brand) 10%, var(--fa-color-surface))',
  '--fa-color-brand-subtle-hover': 'color-mix(in oklab, var(--fa-color-brand) 16%, var(--fa-color-surface))',
};

/**
 * The @foodamigos/ui theme of the platform agent's panel for the dashboard's brand, as CSS custom properties for the
 * panel's root (they never reach the rest of the page):
 * - FoodAmigos: its green as `--fa-brand`; the AI accents keep the design system's FoodAmigos greens.
 * - A partner: the partner's colour as `--fa-brand` and as the AI accent, the accent's second stop a darker shade.
 * - Neutral, or a colour that is no #rrggbb: the design system's monochrome brand and grey accents.
 * `--fa-color-on-brand` is what reads on the brand colour (`onBrand`); the brand's derived colours come with it.
 */
export function platformAgentThemeVars(brand) {
  const color = brand?.primaryColor;

  if (!brand || brand.mode === BRAND_MODES.NEUTRAL || !hexToRgbChannels(color)) {
    return NEUTRAL_AI;
  }

  const brandVars = { '--fa-brand': color, '--fa-color-on-brand': onBrand(color), ...BRAND_COLORS };

  if (brand.mode === BRAND_MODES.FOOD_AMIGOS) {
    return brandVars;
  }

  return {
    ...brandVars,
    '--fa-ai-from': color,
    '--fa-ai-to': `color-mix(in oklab, ${color} 70%, black)`,
  };
}
